import React, {
  FunctionComponent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Noun, Verb } from '@trello/analytics';
import {
  Source,
  Action,
  ActionSubject,
  ActionSubjectId,
} from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { useAtlassianAccountMigrationQuery } from '@trello/graphql/generated';
import { currentLocale } from '@trello/locale';
import Banner from 'app/common/components/banner/banner';
import Icon, { IconColor, IconName } from 'app/common/components/controls/icon';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { HYGIENE_MESSAGE_ID } from 'app/src/components/EmailHygieneWizard/EmailHygieneBanner';
import { track, trackScreenEvent, SnowplowContext } from './Analytics';
import styles from './AtlassianAccountMigrationBanner.less';

const AtlassianAccountMigrationWizardPromise = import(
  /* webpackChunkName: "aa-migration-wizard" */ './AtlassianAccountMigrationWizard'
);
const AtlassianAccountMigrationWizard = lazy(
  () => AtlassianAccountMigrationWizardPromise,
);

let analyticsContext: SnowplowContext;

const format = forNamespace(['aa migration wizard', 'banner'], {
  shouldEscapeStrings: false,
});

const updateByMonth = new Date(2020, 3, 7).toLocaleDateString(currentLocale, {
  month: 'long',
});

export interface AtlassianAccountMigrationBannerProps {
  forceShow?: boolean;
}

export const AtlassianAccountMigrationBanner: FunctionComponent<AtlassianAccountMigrationBannerProps> = ({
  forceShow,
}) => {
  const [shouldShowMigration, setShouldShowMigration] = useState(false);
  const [isOverlayOpen, setOverlayOpen] = useState(false);

  const dataHook = useAtlassianAccountMigrationQuery({
    variables: { memberId: 'me' },
  });

  const me = dataHook.data && dataHook.data.member;

  useEffect(() => {
    if (me) {
      const isPehDone = !!me.oneTimeMessagesDismissed?.includes(
        HYGIENE_MESSAGE_ID,
      );

      const isForced =
        forceShow || /[?&]forceMigration=true/i.test(window.location.search);

      const disabledLocales = ['vi', 'zh-hant', 'zh-hk', 'zh-tw'];

      const needsMigration =
        me.confirmed &&
        me.aaId &&
        me.aaEmail && // Atlassian account is linked
        !me.isAaMastered && // Trello account is not mastered
        !me.aaEnrolledDate && // Account is not enrolled in Access
        !disabledLocales.some((locale) =>
          me.prefs?.locale?.toLowerCase().startsWith(locale),
        ) &&
        featureFlagClient.get('aa4a.show-email-hygiene', false);

      if (isForced || (needsMigration && isPehDone)) {
        setShouldShowMigration(true);
      }
    }
  }, [me, forceShow]);

  const onClickUpdateAccount = useCallback(() => {
    dataHook.refetch();
    setOverlayOpen(true);
    track({
      verb: Verb.INITIATES,
      noun: Noun.AA_MIGRATION_WIZARD,
      method: 'by clicking banner link',
      context: analyticsContext,
      source: Source.atlassianAccountMigrationBanner,
      action: Action.clicked,
      actionSubject: ActionSubject.link,
      actionSubjectId: ActionSubjectId.atlassianAccountMigrationBannerLink,
    });
  }, [dataHook]);

  const onDismiss = useCallback(() => {
    setOverlayOpen(false);
    track({
      verb: Verb.CLOSES,
      noun: Noun.AA_MIGRATION_WIZARD,
      method: 'by dismissing overlay',
      context: analyticsContext,
      source: Source.atlassianAccountMigrationBanner,
      action: Action.closed,
      actionSubject: ActionSubject.overlay,
      actionSubjectId: ActionSubjectId.atlassianAccountMigrationOverlay,
    });
  }, []);

  const onError = useCallback(() => {
    setOverlayOpen(false);
    console.error('Could not migrate. API request failed.');
  }, []);

  if (!analyticsContext && !!me) {
    analyticsContext = {
      organizationId: me.idEnterprise,
      totalEmailAddresses: me.logins?.length,
      totalClaimableEmailAddresses: me.logins?.filter((l) => l.claimable)
        .length,
    };
  }

  useEffect(() => {
    if (shouldShowMigration) {
      trackScreenEvent(
        Source.atlassianAccountMigrationBanner,
        analyticsContext,
      );
    }
  }, [shouldShowMigration]);

  return !shouldShowMigration || !me?.email ? null : (
    <div>
      <Banner className={styles.banner}>
        <p className={styles.text}>
          <Icon
            name={IconName.Information}
            color={IconColor.Yellow}
            large={true}
          ></Icon>
          {format('main-message', { updateByMonth })}
          <a
            href="#"
            className={styles.updateLink}
            onClick={(e) => {
              e.preventDefault();
              onClickUpdateAccount();
            }}
          >
            {format('link-text')}
          </a>
        </p>
        {isOverlayOpen && (
          <Overlay
            background={OverlayBackground.LIGHT}
            closeOnEscape={false}
            onClose={() => {}}
            alignment={OverlayAlignment.TOP}
          >
            <Suspense fallback={null}>
              <AtlassianAccountMigrationWizard
                analyticsContext={analyticsContext}
                onError={onError}
                onDismiss={onDismiss}
                email={me.email}
              />
            </Suspense>
          </Overlay>
        )}
      </Banner>
    </div>
  );
};
